import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Trophy, Heart, Sparkles, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../utils/api';
import { toast } from 'sonner';
import { timeAgo } from '../utils/timeAgo';

const SuccessStories = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSuccessStories();
  }, []);

  const fetchSuccessStories = async () => {
    try {
      const { data } = await api.get('/posts/success-stories');
      setStories(data);
    } catch (error) {
      toast.error('Failed to load success stories');
    } finally {
      setLoading(false);
    }
  };

  const handleCelebrate = async (storyId) => {
    try {
      await api.post(`/posts/${storyId}/celebrate`);
      fetchSuccessStories();
    } catch (error) {
      toast.error('Failed to celebrate');
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-12 gap-6">
          <div className="hidden lg:block lg:col-span-3 sticky top-24 h-fit">
            <Sidebar />
          </div>

          <div className="col-span-12 lg:col-span-9" data-testid="success-stories-container">
            {/* Hero Section */}
            <div className="mb-8 relative overflow-hidden rounded-lg bg-gradient-to-r from-[#0F172A] to-[#1E3A8A] p-8 text-white">
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-3">
                  <Trophy className="h-10 w-10 text-[#22C55E]" />
                  <h1 className="font-oswald text-4xl font-bold uppercase tracking-tight">
                    Success Stories
                  </h1>
                </div>
                <p className="text-lg text-blue-100 max-w-2xl">
                  Celebrating the wins and achievements of the GBA community. Get inspired by your fellow GBAians!
                </p>
              </div>
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#22C55E] opacity-10 rounded-full blur-3xl"></div>
            </div>

            {/* Stats Bar */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              <Card className="p-4 bg-white border border-slate-100 shadow-sm text-center">
                <Trophy className="h-8 w-8 text-[#22C55E] mx-auto mb-2" />
                <p className="text-2xl font-bold text-[#1E3A8A]">{stories.length}</p>
                <p className="text-xs text-slate-600 uppercase tracking-wide">Featured Wins</p>
              </Card>
              <Card className="p-4 bg-white border border-slate-100 shadow-sm text-center">
                <Heart className="h-8 w-8 text-red-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-[#1E3A8A]">
                  {stories.reduce((acc, s) => acc + (s.likes?.length || 0), 0)}
                </p>
                <p className="text-xs text-slate-600 uppercase tracking-wide">Total Likes</p>
              </Card>
              <Card className="p-4 bg-white border border-slate-100 shadow-sm text-center">
                <Sparkles className="h-8 w-8 text-[#22C55E] mx-auto mb-2" />
                <p className="text-2xl font-bold text-[#1E3A8A]">
                  {stories.reduce((acc, s) => acc + (s.celebrates?.length || 0), 0)}
                </p>
                <p className="text-xs text-slate-600 uppercase tracking-wide">Celebrations</p>
              </Card>
            </div>

            {/* Success Stories */}
            {loading ? (
              <div className="text-center py-12">
                <p className="text-slate-500">Loading success stories...</p>
              </div>
            ) : stories.length === 0 ? (
              <Card className="p-12 text-center bg-white">
                <Trophy className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                <h3 className="font-oswald text-xl font-bold text-slate-900 mb-2">
                  No Success Stories Yet
                </h3>
                <p className="text-slate-600">
                  Be the first to share a win! Admins will feature the best stories here.
                </p>
              </Card>
            ) : (
              <div className="space-y-6">
                {stories.map((story, index) => (
                  <motion.div
                    key={story.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    data-testid="success-story-card"
                  >
                    <Card className="bg-white border-l-4 border-l-[#22C55E] shadow-lg hover:shadow-xl transition-all overflow-hidden">
                      <div className="p-6">
                        {/* Header */}
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-4">
                            <Avatar className="h-16 w-16 ring-4 ring-green-100">
                              <AvatarImage src={story.author_photo} />
                              <AvatarFallback className="bg-[#1E3A8A] text-white text-xl">
                                {getInitials(story.author_name)}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <h3 className="font-bold text-xl text-slate-900">
                                {story.author_name}
                              </h3>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
                                  GBA Alumni
                                </Badge>
                                <span className="text-sm text-slate-500">
                                  {timeAgo(story.created_at)}
                                </span>
                              </div>
                            </div>
                          </div>
                          <Badge className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-white flex items-center gap-1">
                            <Trophy className="h-3 w-3" />
                            Featured
                          </Badge>
                        </div>

                        {/* Content */}
                        <div className="mb-6">
                          <p className="text-lg text-slate-800 leading-relaxed whitespace-pre-wrap">
                            {story.content}
                          </p>
                          {story.image_url && (
                            <img
                              src={story.image_url}
                              alt="Success story"
                              className="mt-4 rounded-lg max-w-full h-auto block"
                            />
                          )}
                        </div>

                        {/* Engagement Bar */}
                        <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                          <div className="flex items-center gap-6">
                            <div className="flex items-center gap-2 text-slate-600">
                              <Heart className="h-5 w-5 fill-red-500 text-red-500" />
                              <span className="font-semibold">{story.likes?.length || 0}</span>
                            </div>
                            <div className="flex items-center gap-2 text-slate-600">
                              <Sparkles className="h-5 w-5 fill-green-500 text-green-500" />
                              <span className="font-semibold">{story.celebrates?.length || 0}</span>
                            </div>
                          </div>
                          <Button
                            onClick={() => handleCelebrate(story.id)}
                            className="bg-[#22C55E] hover:bg-[#16A34A] text-white"
                            data-testid="celebrate-story-button"
                          >
                            <Sparkles className="h-4 w-4 mr-2" />
                            Celebrate This Win
                          </Button>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuccessStories;
